import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/contractor-auth/reset-password", { token, password });
      toast.success("Password updated — please sign in.");
      navigate("/contractor/login?reset=1");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/30 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-border bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <Logo className="mb-6" />
          <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-brand">Contractor portal</div>
          <h1 className="mb-6 font-heading text-2xl font-bold tracking-tight text-foreground">Set a new password</h1>

          {!token ? (
            <p className="text-sm text-muted-foreground" data-testid="reset-no-token">
              This reset link is missing its token. Please use the link from your email, or request a new one from the sign-in page.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>New password</Label>
                <div className="relative">
                  <Input data-testid="rp-password" type={showPw ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)} required minLength={6} className="pr-10" />
                  <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Confirm new password</Label>
                <Input data-testid="rp-confirm" type={showPw ? "text" : "password"} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required minLength={6} />
              </div>
              <Button type="submit" className="w-full bg-brand hover:bg-brand-dark" size="lg" disabled={busy} data-testid="rp-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
              </Button>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <Link to="/contractor/login" className="font-semibold text-brand hover:underline">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
